import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import "./index.css";
import "./lib/posthog"; // initializes posthog at module load
import { router } from "./router";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PostHogIdentityBridge } from "./lib/posthog-bridge";
import { shouldRetryQuery, queryRetryDelay } from "./lib/query-retry";
import { captureOnboardingPostureFromUrl } from "./lib/posture";

// Entry-path intent (?posture=...) must be read before the router mounts and
// rewrites the URL to the search params it validates.
captureOnboardingPostureFromUrl();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry only transient connectivity blips (the reconnect race after the
      // laptop wakes) so a failed refetch self-heals instead of blanking the
      // view. Real HTTP errors still fail fast. See lib/query-retry.ts.
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

async function bootstrap() {
  // Dev-only backend mock (src/dev/mock): enabled at /mock or with ?mock=1,
  // answers fetch in-page so the real screens run without a backend. /mock
  // renders its scenario launcher instead of the app. The branch is dead
  // code in production builds, so the module is never bundled there.
  let MockDock: ComponentType<{ history: typeof router.history }> | null = null;
  let MockLauncher: ComponentType | null = null;
  if (import.meta.env.DEV) {
    const mock = await import("./dev/mock");
    mock.syncDevMockFlagFromUrl();
    if (mock.devMockEnabled()) {
      mock.installDevMock();
      if (mock.onMockLauncher()) MockLauncher = mock.MockLauncher;
      else MockDock = mock.MockDock;
    }
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      {MockLauncher ? (
        <MockLauncher />
      ) : (
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <PostHogIdentityBridge />
            <RouterProvider router={router} />
            {MockDock && <MockDock history={router.history} />}
            <Toaster richColors closeButton position="top-right" />
          </TooltipProvider>
        </QueryClientProvider>
      )}
    </StrictMode>
  );
}

void bootstrap();
